// IIFE : les assets du thème sont des scripts classiques, donc tout `const`/`function` de premier
// niveau atterrit dans la MÊME portée globale. `isFullCart` était déclaré ici ET dans
// cart-tiers.js : le second fichier chargé mourait avec « Identifier 'isFullCart' has already been
// declared » et aucun cadeau n'était jamais ajouté. On isole plutôt que de renommer, sinon la
// prochaine collision (fetchCartState, updateCartLine, qualifyingSubtotal…) reproduit la panne.
(() => {
let cartOfferSyncPromise;
let cartOfferPendingSync;

class CartOffer extends HTMLElement {
  constructor() {
    super();
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    const configElement = this.querySelector('[data-offer-config]');
    if (!configElement) return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('[Cart Offer] Failed to parse config:', error);
      return;
    }

    // Toujours synchroniser, même offre désactivée : c'est ce qui retire les cadeaux restés
    // au panier après une désactivation ou la suppression d'un palier.
    queueMicrotask(() => syncCartOffer(this.config));

    if (!this.config.active) return;

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    // Le panier est partagé entre onglets : plusieurs onglets ouverts d'un coup ajoutent chacun
    // leur cadeau. Resynchroniser au retour sur l'onglet corrige avant que le client ne commande.
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  onCartUpdate(event) {
    if (!this.config?.active) return;
    if (event.source === 'cart-offer') return;

    return syncCartOffer(this.config);
  }

  onVisibilityChange() {
    if (document.visibilityState === 'visible') syncCartOffer(this.config);
  }
}

if (!customElements.get('cart-offer')) {
  customElements.define('cart-offer', CartOffer);
}

function syncCartOffer(config) {
  if (cartOfferSyncPromise) {
    cartOfferPendingSync = { config };
    return cartOfferSyncPromise;
  }

  cartOfferSyncPromise = performCartOfferSync(config)
    .catch((error) => {
      console.error('[Cart Offer] Sync error:', error);
    })
    .finally(() => {
      cartOfferSyncPromise = null;

      if (!cartOfferPendingSync) return;

      const nextSync = cartOfferPendingSync;
      cartOfferPendingSync = null;
      setTimeout(() => syncCartOffer(nextSync.config), 0);
    });

  return cartOfferSyncPromise;
}

async function performCartOfferSync(config) {
  // Toujours relire le panier, jamais le `cartData` de l'événement : une réponse /cart/change
  // partie avant notre ajout arrive sans le cadeau, et on en ajoutait un second (même variante
  // + mêmes propriétés = Shopify cumule la quantité sur la même ligne).
  const cartState = await fetchCartState();
  const items = Array.isArray(cartState.items) ? cartState.items : [];
  const tiers = Array.isArray(config.tiers) ? config.tiers : [];
  // Palier par variante cadeau : une ligne ajoutée hors moteur (fiche produit, « commander à
  // nouveau »…) n'a pas _offer_gift / _offer_id. Elle est rattachée à son palier par la variante,
  // sinon elle est invisible : jamais retirée sous le seuil, jamais plafonnée à 1.
  const tierIdByVariant = new Map(tiers.map((tier) => [tier.giftVariantId, tier.id]));

  const subtotal = qualifyingSubtotal(cartState, tierIdByVariant);
  const eligibleTierIds = new Set(
    config.active && productsConditionMet(config, items, tierIdByVariant)
      ? tiers.filter((tier) => subtotal >= tier.thresholdCents).map((tier) => tier.id)
      : []
  );

  // Cible : exactement une ligne, quantité 1, par palier éligible. Part tout le reste : palier
  // plus éligible (seuil, éditeur, offre désactivée) et doublons (courses entre onglets).
  const keptTierIds = new Set();
  const removals = [];
  const resets = [];
  for (const giftLine of findGiftLines(items, tierIdByVariant)) {
    if (!eligibleTierIds.has(giftLine.offerId) || keptTierIds.has(giftLine.offerId)) {
      removals.push(giftLine);
      continue;
    }
    keptTierIds.add(giftLine.offerId);
    if (giftLine.quantity !== 1) resets.push(giftLine);
  }
  const additions = tiers.filter((tier) => eligibleTierIds.has(tier.id) && !keptTierIds.has(tier.id));

  if (removals.length === 0 && resets.length === 0 && additions.length === 0) return;

  await setGiftLinesQuantity(removals, 0);
  await setGiftLinesQuantity(resets, 1);

  for (const tier of additions) {
    await addGiftVariant(tier.giftVariantId, 1, tier.id);
  }

  await publishLatestCart();
}

const isGiftLine = (item, tierIdByVariant) =>
  item?.properties?._offer_gift === 'true' || tierIdByVariant.has(item?.variant_id);

// Don (LP-20) : exclu du sous-total qualifiant, un don ne débloque aucun avantage.
const isDonationLine = (item) => item?.properties?._donation === 'true';

// Sous-total qualifiant : total APRÈS toutes les remises (ligne et panier), moins TOUTES les lignes
// cadeau, sinon le cadeau d'un palier gonfle le sous-total du palier suivant. Doit rester
// identique à cart-tiers.liquid et cart-tiers.js (qui ne connaissent que _offer_gift : une ligne
// cadeau sans propriété vaut 0 €, donc le résultat est le même).
function qualifyingSubtotal(cart, tierIdByVariant) {
  const items = Array.isArray(cart.items) ? cart.items : [];
  return items.reduce(
    (sum, item) =>
      isGiftLine(item, tierIdByVariant) || isDonationLine(item) ? sum - (item.final_line_price || 0) : sum,
    cart.total_price || 0
  );
}

function findGiftLines(items, tierIdByVariant) {
  return items
    .filter((item) => isGiftLine(item, tierIdByVariant))
    .map((item) => ({
      key: item.key,
      quantity: item.quantity || 0,
      offerId: item.properties?._offer_id ?? tierIdByVariant.get(item.variant_id),
    }));
}

// Condition produits globale, appliquée à tous les paliers. Liste vide = pas de condition :
// le montant du palier suffit. (Avant, un select à trois valeurs pouvait exiger 32 produits
// et désactivait silencieusement tous les paliers.)
function productsConditionMet(config, items, tierIdByVariant) {
  const requiredProductIds = Array.isArray(config.requiredProductIds)
    ? config.requiredProductIds.map(Number)
    : [];
  if (requiredProductIds.length === 0) return true;

  const productIdsInCart = new Set(
    items.filter((item) => !isGiftLine(item, tierIdByVariant)).map((item) => Number(item.product_id))
  );

  return config.requireAllProducts
    ? requiredProductIds.every((productId) => productIdsInCart.has(productId))
    : requiredProductIds.some((productId) => productIdsInCart.has(productId));
}

async function addGiftVariant(variantId, quantity, offerId) {
  if (!variantId || quantity <= 0) return;

  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', quantity);
  formData.append('properties[_offer_gift]', 'true');
  formData.append('properties[_offer_id]', offerId);

  const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  if (cartUI) {
    formData.append(
      'sections',
      cartUI.getSectionsToRender().map((section) => section.id),
    );
    formData.append('sections_url', window.location.pathname);
    if (typeof cartUI.setActiveElement === 'function') {
      cartUI.setActiveElement(document.activeElement);
    }
  }

  config.body = formData;

  const response = await fetch(routes.cart_add_url, config);
  const parsedState = await response.json();

  if (parsedState.status) {
    throw new Error(parsedState.description || parsedState.message || 'Failed to add offer gift to cart');
  }

  if (cartUI && parsedState.sections && typeof cartUI.renderContents === 'function') {
    cartUI.renderContents(parsedState);
  }

  return parsedState;
}

// Par clé de ligne et non par index : la clé ne bouge pas quand une autre ligne est retirée.
async function setGiftLinesQuantity(giftLines, quantity) {
  for (const giftLine of giftLines) {
    try {
      const body = JSON.stringify({ id: giftLine.key, quantity });
      const response = await fetch(routes.cart_change_url, { ...fetchConfig(), body });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      console.error('[Cart Offer] Failed to set gift line quantity:', giftLine.key, quantity, error);
    }
  }
}

async function fetchCartState() {
  const response = await fetch(`${routes.cart_url}.js`);
  return response.json();
}

async function publishLatestCart() {
  const latestCart = await fetchCartState();
  await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-offer', cartData: latestCart });
}
})();
